import NoImage from 'assets/images/others/no-image.png';
import { decompressFromBase64 } from 'lz-string';
import React, { FC, Suspense, useMemo, useState, useTransition } from 'react';
import { commitLocalUpdate, graphql, useLazyLoadQuery, useMutation, useRelayEnvironment } from 'react-relay';
import { useNavigate } from 'react-router-dom';
import config from 'src/shared/config';
import { useRelayStore } from 'src/relay/environment';
import { T, backofficeUrl, toGlobalID } from 'src/shared/helpers';
import type { productSidePanelContent_ProductQuery } from './__generated__/productSidePanelContent_ProductQuery.graphql';
import { productSidePanelContent_RemoveAIStagingMutation } from './__generated__/productSidePanelContent_RemoveAIStagingMutation.graphql';
import { productSidePanelContent_RemoveAIVirtualModelMutation } from './__generated__/productSidePanelContent_RemoveAIVirtualModelMutation.graphql';
import { productSidePanelContent_RemoveAreaBgMutation } from './__generated__/productSidePanelContent_RemoveAreaBgMutation.graphql';
import { productSidePanelContent_RemoveGridRuleMutation } from './__generated__/productSidePanelContent_RemoveGridRuleMutation.graphql';
import { productSidePanelContent_RemoveMoqMutation } from './__generated__/productSidePanelContent_RemoveMoqMutation.graphql';
import { productSidePanelContent_RemovePricingMutation } from './__generated__/productSidePanelContent_RemovePricingMutation.graphql';
import { productSidePanelContent_RemoveProfanityMutation } from './__generated__/productSidePanelContent_RemoveProfanityMutation.graphql';
import type { productSidePanelContent_DisableBulkVariationMutation } from './__generated__/productSidePanelContent_DisableBulkVariationMutation.graphql';
import type { productSidePanelContent_UnassignShapesMutation } from './__generated__/productSidePanelContent_UnassignShapesMutation.graphql';
import type { productSidePanelContent_RemoveQuoteRuleMutation } from './__generated__/productSidePanelContent_RemoveQuoteRuleMutation.graphql';
import type { productSidePanelContent_SetStatusProductMutation } from './__generated__/productSidePanelContent_SetStatusProductMutation.graphql';
import type { productSidePanelContent_ToolDataQuery } from './__generated__/productSidePanelContent_ToolDataQuery.graphql';
import type { productSidePanelContent_UnassignPrintMethodMutation } from './__generated__/productSidePanelContent_UnassignPrintMethodMutation.graphql';
import { useDialogManager } from 'src/components/widgets/dialog';
import { DisconnectProductSalesChannel } from '../productSalesChannelList';
import { ProductSalesChannelInfo, TOOL_DEFINITIONS, getAllSalesChannels, getSalesChannelCount, getToolLabel } from './common/constants';
import {
    FooterButton,
    HeaderMeta,
    HeaderPreviewButton,
    HeaderSep,
    PanelFooter,
    PanelHeaderContent,
    PanelHeaderImg,
    PanelHeaderInfo,
    SidePanelDrawer,
    SidePanelScrollArea,
    StatusBadge,
    ToolBadge
} from './common/styled';
import type { AddPanelKey, ProductSidePanelProps, ProductToolData, SidePanelProduct, StackedEditor } from './common/types';
import { AddRulePanels } from './sections/addRulePanels';
import { ConfiguratorStructureSection } from './sections/configuratorStructureSection';
import { DeleteConfirmationOverlay } from './sections/deleteConfirmationOverlay';
import { FullScreenOverview } from './sections/fullScreenOverview';
import { CustomizerStructureSection } from './sections/productStructureSection';
import { StackedEditorPanel } from './sections/stackedEditorPanel';
import { StatusBarSection } from './sections/statusBarSection';
import ToolCategorySections from './sections/toolCategorySections';
import { ViewerStructureSection } from './sections/viewerStructureSection';
import { HelpArticlePanel } from './sections/helpArticlePanel';

// ========== Queries ==========
const sidePanelProductQuery = graphql`
    query productSidePanelContent_ProductQuery($id: ID!) {
        model(id: $id) {
            id
            modelID
            name
            code
            statusID
            smallImageUrl
            zakekeEnvironment
            provider
            canAddAdditionalCode
            sourceProductTemplateID
            modelColorsCompressed
            salesChannel {
                id
                detail {
                    id
                    properties {
                        name
                    }
                }
                ecommerceSettings {
                    ecommerceName
                    remoteUrl
                    productsApi
                    hasProductsApi
                    definitiveUrl
                }
            }
            additionalCodes {
                code
                salesChannelID
                merchantProductName
                salesChannel {
                    id
                    detail {
                        id
                        properties {
                            name
                        }
                    }
                    ecommerceSettings {
                        ecommerceName
                        remoteUrl
                        productsApi
                        hasProductsApi
                        definitiveUrl
                    }
                }
            }
            printTypes {
                printTypeID
                name
                printTypeRestrictions {
                    disableSellerImages
                    disableUserImages
                }
            }
            scenes {
                sceneID
            }
            areaBackgroundRules {
                productRule {
                    colorList
                }
            }
            quoteRule {
                id
                quoteRuleID
                name
            }
            moqRules {
                productRule {
                    id
                    moqRuleID
                    name
                }
            }
        }
    }
`;

const sidePanelToolDataQuery = graphql`
    query productSidePanelContent_ToolDataQuery($productId: Long!, $modelId: ID!, $pricingFirst: Int!) {
        designTemplates(productId: $productId) {
            designTemplateId
            name
            previewUrl
        }
        presetConfigurationsByProduct(productID: $modelId) {
            id
            compositionID
            name
            previewImageUrl
        }
        shapesAssignmentsForProduct(productID: $productId) {
            forProduct
            printTypesIDs
        }
        shapesAssignments {
            productsIDs
            printTypesIDs
        }
        hasShapes
        shapesCount
        pricingsDetailed(productID: $productId, first: $pricingFirst) {
            edges {
                node {
                    id
                    pricingName
                    pricingID
                }
            }
        }
        profanityFilterRulesForProduct(productID: $productId) {
            productRule {
                ruleID
                ruleName
            }
            printTypeRules {
                key
                value {
                    ruleID
                    ruleName
                }
            }
        }
        aiProductStagingRulesByProduct(productID: $productId) {
            id
            name
        }
        aiVirtualModelRulesByProduct(productID: $productId) {
            id
            name
        }
        gridRulesByProduct(productID: $productId) {
            id
            name
        }
        areaBackgroundRulesByProduct(productID: $productId) {
            id
            name
        }
        additionalAttributesByProduct(productID: $modelId) {
            id
            additionalAttributeID
            name
        }
        myAdditionalAttributes(status: 0) {
            id
        }
        profanityFilterRulesCount
        printTypes(first: 200) {
            edges {
                node {
                    id
                    printTypeID
                    name
                }
            }
        }
        pricings(first: 200) {
            edges {
                node {
                    id
                    pricingName
                    pricingID
                }
            }
        }
        areaBackgroundRulesCount
        gridRulesCount
        placementsOutput(id: $modelId) {
            id
            placementOutputID
            name
            isEnabled
        }
        imageGeneration {
            id
            serviceIDGeneration
            serviceIDEdit
        }
        designWithAISetting {
            settingsID
        }
        productMultipleVariantsRule(productId: $modelId) {
            attributeCodes
            nameAndNumberEnabled
            skipOptionForNameAndNumber
            bulkAsNameAndNumberEnabled
            enableImportCSV
            options
        }
        aiProductStagingRulesCount
        aiVirtualModelRulesCount
        moqRulesCount
        quoteRulesCount
        imageMacroCategories(onlybackground: false) {
            hasImages
        }
        extensionFields {
            id
            name
        }
    }
`;
export const ProductSidePanelContent: FC<ProductSidePanelProps> = ({
    productId,
    onClose,
    onDelete,
    onPreview,
    onPublishToggle,
    onDuplicate,
    onAddSalesChannel,
    onToolAssignmentChange
}) => {
    // ========== Hooks ==========
    const navigate = useNavigate();
    const relayEnvironment = useRelayEnvironment();
    const { showDialog } = useDialogManager();

    // ========== UI State ==========
    const [channelsOpen, setChannelsOpen] = useState(false);
    const [expandAllKey, setExpandAllKey] = useState(0);
    const [openSections, setOpenSections] = useState<Set<string>>(new Set());
    const [hideUnconfigured, setHideUnconfigured] = useState(() => {
        try {
            return localStorage.getItem('zakeke_hideUnconfiguredTools') === 'true';
        } catch {
            return false;
        }
    });
    const [confirmingDelete, setConfirmingDelete] = useState(false);
    const [optimisticStatusId, setOptimisticStatusId] = useState<number | null>(null);
    const [isPublishing, setIsPublishing] = useState(false);

    // ========== Panel & Editor State ==========
    const [stackedEditor, setStackedEditor] = useState<StackedEditor | null>(null);
    const [activeAddPanel, setActiveAddPanel] = useState<AddPanelKey | null>(null);
    const [helpPanel, setHelpPanel] = useState<{ articleId: string; helpUrl: string } | null>(null);

    // ========== Data Refresh State ==========
    const [dataRefreshKey, setDataRefreshKey] = useState(0);
    const [unassigningItemId, setUnassigningItemId] = useState<string | null>(null);
    const [isUnassigning, startUnassignTransition] = useTransition();

    // ========== Mutations ==========

    const [commitSetStatus] = useMutation<productSidePanelContent_SetStatusProductMutation>(graphql`
        mutation productSidePanelContent_SetStatusProductMutation($input: SetStatusProductInput!) {
            setStatusProduct(input: $input) {
                model {
                    id
                    statusID
                }
                errors {
                    ... on LiquidSintaxError {
                        message
                    }
                }
            }
        }
    `);

    const [commitUnassignPrintMethod] = useMutation<productSidePanelContent_UnassignPrintMethodMutation>(graphql`
        mutation productSidePanelContent_UnassignPrintMethodMutation($input: AddOrRemoveAssignmentPrintTypeToProductsInput!) {
            addOrRemoveAssignmentPrintTypeToProducts(input: $input)
        }
    `);

    const [commitRemovePricing] = useMutation<productSidePanelContent_RemovePricingMutation>(graphql`
        mutation productSidePanelContent_RemovePricingMutation($pricingId: ID!, $productIds: [Long!]!) {
            removeProductsFromPricing(pricingId: $pricingId, productIds: $productIds) {
                result
            }
        }
    `);

    const [commitRemoveMoq] = useMutation<productSidePanelContent_RemoveMoqMutation>(graphql`
        mutation productSidePanelContent_RemoveMoqMutation($moqRuleId: ID!, $productIds: [Long!]!) {
            removeProductsFromMoqRule(moqRuleId: $moqRuleId, productIds: $productIds) {
                result
            }
        }
    `);

    const [commitRemoveAIStaging] = useMutation<productSidePanelContent_RemoveAIStagingMutation>(graphql`
        mutation productSidePanelContent_RemoveAIStagingMutation($ruleId: ID!, $productIds: [Long!]!) {
            removeProductsFromAIStagingRule(ruleId: $ruleId, productIds: $productIds) {
                result
            }
        }
    `);

    const [commitRemoveAIVirtualModel] = useMutation<productSidePanelContent_RemoveAIVirtualModelMutation>(graphql`
        mutation productSidePanelContent_RemoveAIVirtualModelMutation($ruleId: ID!, $productIds: [Long!]!) {
            removeProductsFromAIVirtualModelRule(ruleId: $ruleId, productIds: $productIds) {
                result
            }
        }
    `);

    const [commitRemoveProfanity] = useMutation<productSidePanelContent_RemoveProfanityMutation>(graphql`
        mutation productSidePanelContent_RemoveProfanityMutation($profanityRuleId: ID!, $productIds: [Long!]!) {
            removeProductsFromProfanityRule(profanityRuleId: $profanityRuleId, productIds: $productIds) {
                result
            }
        }
    `);

    const [commitRemoveGridRule] = useMutation<productSidePanelContent_RemoveGridRuleMutation>(graphql`
        mutation productSidePanelContent_RemoveGridRuleMutation($gridRuleId: ID!, $productIds: [Long!]) {
            removeProductsFromGridRule(gridRuleId: $gridRuleId, productIds: $productIds) {
                result
            }
        }
    `);

    const [commitRemoveAreaBg] = useMutation<productSidePanelContent_RemoveAreaBgMutation>(graphql`
        mutation productSidePanelContent_RemoveAreaBgMutation($ruleId: ID!, $productIds: [Long!]!) {
            removeProductsFromAreaBackgroundRule(ruleId: $ruleId, productIds: $productIds) {
                result
            }
        }
    `);

    const [commitRemoveQuoteRule] = useMutation<productSidePanelContent_RemoveQuoteRuleMutation>(graphql`
        mutation productSidePanelContent_RemoveQuoteRuleMutation($ruleId: ID!, $productIds: [Long!]!) {
            removeProductsFromQuoteRule(ruleId: $ruleId, productIds: $productIds) {
                result
            }
        }
    `);

    const [commitDisableBulkVariation] = useMutation<productSidePanelContent_DisableBulkVariationMutation>(graphql`
        mutation productSidePanelContent_DisableBulkVariationMutation($id: ID!, $input: ProductMultipleVariantsRuleInput!) {
            updateProductMultipleVariantsRule(productId: $id, input: $input) {
                attributeCodes
                nameAndNumberEnabled
                skipOptionForNameAndNumber
                bulkAsNameAndNumberEnabled
                enableImportCSV
                options
            }
        }
    `);

    const [commitUnassignShapes] = useMutation<productSidePanelContent_UnassignShapesMutation>(graphql`
        mutation productSidePanelContent_UnassignShapesMutation($productsIds: [ID!]!, $printTypesIds: [ID!]!) {
            assignShapes(productsIds: $productsIds, printTypesIds: $printTypesIds)
        }
    `);

    const [commitEnablePlacement] = useMutation(graphql`
        mutation productSidePanelContent_EnablePlacementMutation($id: ID!, $isEnabled: Boolean!) {
            enablePlacementOutput(id: $id, isEnabled: $isEnabled) {
                placementOutput {
                    id
                    isEnabled
                }
            }
        }
    `);

    // ========== Data Queries ==========
    const data = useLazyLoadQuery<productSidePanelContent_ProductQuery>(
        sidePanelProductQuery,
        {
            id: productId
        },
        { fetchPolicy: 'network-only', fetchKey: dataRefreshKey }
    );
    const product = data.model as SidePanelProduct;

    // ========== Derived Data (Product) ==========

    const allProductColors = React.useMemo(() => {
        const compressed = product?.modelColorsCompressed;
        if (!compressed) return [];
        try {
            return JSON.parse(decompressFromBase64(compressed)) as {
                colorID: number;
                name: string;
                code: string | null;
                colorCode: string | null;
                sides?: {
                    sideID?: number;
                    name?: string;
                    imageUrl?: string;
                    ppcm?: number;
                    areas?: { name?: string; boundsWidth?: number; boundsHeight?: number }[];
                }[];
            }[];
        } catch {
            return [];
        }
    }, [product?.modelColorsCompressed]);

    const productColors = React.useMemo(() => allProductColors.filter((color) => color.colorID !== 1), [allProductColors]);

    const productStructureSummary = React.useMemo(() => {
        const totalVariants = productColors.length;
        const source = allProductColors.length > 0 ? allProductColors : [];
        const maxSides = source.reduce((max, color) => Math.max(max, color.sides?.length || 0), 0);
        const maxPrintAreas = source.reduce((max, color) => {
            const variantAreas = (color.sides || []).reduce((sum, side) => sum + (side.areas?.length || 0), 0);
            return Math.max(max, variantAreas);
        }, 0);
        return { totalVariants, maxSides, maxPrintAreas };
    }, [productColors, allProductColors]);

    const toolDataQuery = useLazyLoadQuery<productSidePanelContent_ToolDataQuery>(
        sidePanelToolDataQuery,
        {
            productId: product.modelID,
            modelId: product.id,
            pricingFirst: 50
        },
        { fetchPolicy: 'network-only', fetchKey: dataRefreshKey }
    );

    // ========== Derived Data (Tool Data) ==========

    const accountPrintMethods = useMemo(() => {
        const edges = toolDataQuery?.printTypes?.edges || [];
        return edges.map((edge) => ({
            id: edge.node.id,
            numericId: String(edge.node.printTypeID),
            name: edge.node.name
        }));
    }, [toolDataQuery]);

    const accountPricingRules = useMemo(() => {
        const edges = toolDataQuery?.pricings?.edges || [];
        return edges.map((edge) => ({
            id: edge.node.id,
            name: edge.node.pricingName || `#${edge.node.pricingID}`
        }));
    }, [toolDataQuery]);

    const accountHasRules = useMemo<Record<string, boolean>>(
        () => ({
            'printing-methods': (accountPrintMethods?.length ?? 0) > 0,
            pricing: (accountPricingRules?.length ?? 0) > 0,
            profanity: (toolDataQuery?.profanityFilterRulesCount ?? 0) > 0,
            'area-background': (toolDataQuery?.areaBackgroundRulesCount ?? 0) > 0,
            collage: (toolDataQuery?.gridRulesCount ?? 0) > 0,
            'ai-lifestyle-scenes': (toolDataQuery?.aiProductStagingRulesCount ?? 0) > 0,
            'ai-try-on': (toolDataQuery?.aiVirtualModelRulesCount ?? 0) > 0,
            moq: (toolDataQuery?.moqRulesCount ?? 0) > 0,
            'get-a-quote': (toolDataQuery?.quoteRulesCount ?? 0) > 0,
            'extra-options': (toolDataQuery?.myAdditionalAttributes?.length ?? 0) > 0
        }),
        [toolDataQuery, accountPrintMethods, accountPricingRules]
    );

    const placements = useMemo(
        () =>
            (toolDataQuery?.placementsOutput || []).map((p) => ({
                id: p.id,
                name: p.name ?? '',
                isEnabled: p.isEnabled
            })),
        [toolDataQuery]
    );
    const aiImageGenConfigured = useMemo(() => {
        const img = toolDataQuery?.imageGeneration;
        return img?.serviceIDGeneration != null || img?.serviceIDEdit != null;
    }, [toolDataQuery]);
    const aiDesignGenConfigured = useMemo(() => toolDataQuery?.designWithAISetting != null, [toolDataQuery]);
    const bulkVariationConfigured = useMemo(() => (toolDataQuery?.productMultipleVariantsRule?.attributeCodes || []).length > 0, [toolDataQuery]);

    const toolData = React.useMemo<ProductToolData>(() => {
        const moqRule = product.moqRules?.productRule;
        return {
            loaded: true,
            templates: (toolDataQuery.designTemplates || []).map((template) => ({
                id: template.designTemplateId.toString(),
                name: template.name,
                previewUrl: template.previewUrl
            })),
            presetConfigurations: (toolDataQuery.presetConfigurationsByProduct || []).map((preset) => ({
                id: preset.id,
                name: preset.name || `#${preset.compositionID}`,
                previewUrl: preset.previewImageUrl ?? null
            })),
            shapesConfigured: !!(
                (toolDataQuery.shapesCount ?? 0) > 0 &&
                (toolDataQuery.shapesAssignmentsForProduct?.forProduct || (toolDataQuery.shapesAssignmentsForProduct?.printTypesIDs?.length ?? 0) > 0)
            ),
            hasShapes: (toolDataQuery.shapesCount ?? 0) > 0,
            pricingRules: (toolDataQuery.pricingsDetailed?.edges || []).map((edge) => ({
                id: edge.node.id,
                name: edge.node.pricingName || `#${edge.node.pricingID}`
            })),
            profanityRules: (() => {
                const rule = toolDataQuery.profanityFilterRulesForProduct?.productRule;
                if (!rule) return [];
                return [{ id: toGlobalID(rule.ruleID, 'ProfanityRule'), name: rule.ruleName || `#${rule.ruleID}` }];
            })(),
            aiStagingRules: (toolDataQuery.aiProductStagingRulesByProduct || []).map((rule) => ({ id: rule.id, name: rule.name })),
            aiVirtualModelRules: (toolDataQuery.aiVirtualModelRulesByProduct || []).map((rule) => ({ id: rule.id, name: rule.name })),
            has3DScene: !!(product.scenes?.length && product.scenes[0]?.sceneID),
            areaBackgroundRules: (toolDataQuery.areaBackgroundRulesByProduct || []).map((rule) => ({
                id: rule.id,
                name: rule.name || ''
            })),
            quoteRule: product.quoteRule ? { id: product.quoteRule.id, name: product.quoteRule.name || `#${product.quoteRule.quoteRuleID}` } : null,
            moqRules: moqRule ? [{ id: moqRule.id, name: moqRule.name || `#${moqRule.moqRuleID}` }] : [],
            collageRules: (toolDataQuery.gridRulesByProduct || []).filter(Boolean).map((rule) => ({
                id: rule!.id!,
                name: rule!.name || ''
            })),
            bulkVariationEnabled: bulkVariationConfigured,
            extraOptions: toolDataQuery.additionalAttributesByProduct
                ? [
                      {
                          id: String(toolDataQuery.additionalAttributesByProduct.additionalAttributeID),
                          name: toolDataQuery.additionalAttributesByProduct.name || `#${toolDataQuery.additionalAttributesByProduct.additionalAttributeID}`
                      }
                  ]
                : [],
            placements,
            aiImageGenConfigured,
            aiDesignGenConfigured,
            imageRestrictions: (product.printTypes || []).map((pt) => ({
                printMethodId: String(pt.printTypeID),
                printMethodName: pt.name,
                userUpload: !(pt.printTypeRestrictions?.disableUserImages ?? false),
                clipartGalleries: !(pt.printTypeRestrictions?.disableSellerImages ?? false)
            })),
            hasGalleryContent: (toolDataQuery.imageMacroCategories || []).some((cat) => cat.hasImages),
            extensionFields: (toolDataQuery.extensionFields || []).map((ef) => ({
                id: ef!.id,
                name: ef!.name || `#${ef!.id}`
            }))
        };
    }, [toolDataQuery, product, placements, aiImageGenConfigured, aiDesignGenConfigured, bulkVariationConfigured]);

    if (!product) return null;

    // ========== Computed Values ==========
    const isOnline = (optimisticStatusId ?? product.statusID) === 1;
    const salesChannels = getAllSalesChannels(product);
    const salesChannelCount = getSalesChannelCount(product);
    const availableTools = TOOL_DEFINITIONS.filter((t) => t.environments.includes(product.zakekeEnvironment));
    const isShopify = product.salesChannel.ecommerceSettings?.ecommerceName === 'SHOPIFY';
    const isDuplicateEnabled =
        product.zakekeEnvironment !== 'VIEWER' && product.provider === 'ZAKEKE' && product.salesChannel.ecommerceSettings?.ecommerceName !== 'NONE';
    const hasSalesChannels = salesChannelCount > 0;
    const canAddSalesChannel =
        !!onAddSalesChannel &&
        product.canAddAdditionalCode &&
        (product.salesChannel.ecommerceSettings?.ecommerceName !== 'NONE' || product.additionalCodes.length >= 1) &&
        product.provider === 'ZAKEKE' &&
        product.zakekeEnvironment !== 'COMPOSER';
    const salesChannelAction = !hasSalesChannels && onPublishToggle ? onPublishToggle : hasSalesChannels && canAddSalesChannel ? onAddSalesChannel : undefined;
    const salesChannelActionLabel = !hasSalesChannels ? (
        isOnline ? (
            T._('Unpublish', 'Admin')
        ) : (
            T._('Publish', 'Admin')
        )
    ) : (
        <>+ {T._('Add to a Sales Channel', 'Admin')}</>
    );

    const hasOptionPrice = (opt: { price: number | null; optionCode: string }): boolean => {
        if (!isShopify) {
            return !!(opt.price && opt.price > 0);
        }

        try {
            const parsed = JSON.parse(opt.optionCode);
            return parsed?.zakekePlatform === true;
        } catch {
            return false;
        }
    };

    // ========== Event Handlers ==========

    const handleEditProduct = () => {
        if (product.zakekeEnvironment === 'COMPOSER') {
            window.location.href = `/Composer?id=${product.modelID}&page=product`;
        } else if (product.zakekeEnvironment === 'VIEWER') {
            window.location.href = '/' + config.culture + '/Admin/DAM?page=/gallery';
        } else {
            navigate(backofficeUrl('/product?product_id=' + product.modelID));
        }
    };

    const handleOpenToolEditor = (toolKey: string, itemId: string) => {
        setStackedEditor({ type: toolKey, name: toolKey, id: itemId });
    };

    const handleEnablePlacement = (placementId: string, isEnabled: boolean) => {
        commitEnablePlacement({
            variables: { id: placementId, isEnabled },
            updater: (store) => store.invalidateStore(),
            onCompleted: () => setDataRefreshKey((k) => k + 1)
        });
    };

    const handleCloseStacked = () => {
        setStackedEditor(null);
        // Mutations in the iframe run in a separate Relay environment, so the parent's
        // useLazyLoadQuery fetchKey mechanism doesn't reliably pick up new data. Recreating
        // the environment forces every query to refetch from network.
        useRelayStore.getState().incrementRelayStoreKey();
    };

    const sidebarNavState = { fromSidebar: true, sidebarProductId: productId };

    const handleDisconnectSalesChannel = (channel: ProductSalesChannelInfo) => {
        showDialog(
            'delete-channel',
            <DisconnectProductSalesChannel
                productID={product.id}
                sku={channel.productCode}
                salesChannelID={channel.salesChannelID}
                onRemoveCompleted={() => {
                    commitLocalUpdate(relayEnvironment, (store) => store.invalidateStore());
                    setDataRefreshKey((k) => k + 1);
                }}
            />
        );
    };

    const handleGoToFullTool = (route: string, id?: string) => {
        if (id) {
            navigate(backofficeUrl(route + '/' + id), { state: sidebarNavState });
        } else {
            navigate(backofficeUrl(route), { state: sidebarNavState });
        }
    };

    const handlePublishToggle = async () => {
        if (isPublishing) return;

        if (!hasSalesChannels && onPublishToggle) {
            onPublishToggle();
            return;
        }

        const newStatusId = (optimisticStatusId ?? product.statusID) === 1 ? 0 : 1;
        setOptimisticStatusId(newStatusId);
        setIsPublishing(true);
        commitSetStatus({
            variables: { input: { productID: product.id, status: newStatusId === 1 ? 'PUBLISHED' : 'UN_PUBLISHED' } },
            onCompleted: () => {
                setIsPublishing(false);
            },
            onError: () => {
                setOptimisticStatusId(product.statusID);
                setIsPublishing(false);
            }
        });
    };

    const handleUnassignTool = (toolKey: string, itemId: string, _itemName: string) => {
        setUnassigningItemId(itemId);

        const onDone = () => {
            setUnassigningItemId(null);
            startUnassignTransition(() => setDataRefreshKey((k) => k + 1));
        };

        const updater = (store: any) => store.invalidateStore();

        const onFail = (error: Error) => {
            console.error(`Failed to unassign ${toolKey}:`, error);
            setUnassigningItemId(null);
        };

        if (toolKey === 'printing-methods') {
            commitUnassignPrintMethod({
                variables: { input: { printTypeID: itemId, productIDsToRemove: [product.id] } },
                updater,
                onCompleted: onDone,
                onError: onFail
            });
        } else if (toolKey === 'pricing') {
            commitRemovePricing({ variables: { pricingId: itemId, productIds: [product.modelID] }, updater, onCompleted: onDone, onError: onFail });
        } else if (toolKey === 'moq') {
            commitRemoveMoq({ variables: { moqRuleId: itemId, productIds: [product.modelID] }, updater, onCompleted: onDone, onError: onFail });
        } else if (toolKey === 'ai-lifestyle-scenes') {
            commitRemoveAIStaging({ variables: { ruleId: itemId, productIds: [product.modelID] }, updater, onCompleted: onDone, onError: onFail });
        } else if (toolKey === 'ai-try-on') {
            commitRemoveAIVirtualModel({ variables: { ruleId: itemId, productIds: [product.modelID] }, updater, onCompleted: onDone, onError: onFail });
        } else if (toolKey === 'profanity') {
            commitRemoveProfanity({ variables: { profanityRuleId: itemId, productIds: [product.modelID] }, updater, onCompleted: onDone, onError: onFail });
        } else if (toolKey === 'collage') {
            commitRemoveGridRule({ variables: { gridRuleId: itemId, productIds: [product.modelID] }, updater, onCompleted: onDone, onError: onFail });
        } else if (toolKey === 'area-background') {
            commitRemoveAreaBg({ variables: { ruleId: itemId, productIds: [product.modelID] }, updater, onCompleted: onDone, onError: onFail });
        } else if (toolKey === 'get-a-quote') {
            commitRemoveQuoteRule({
                variables: { ruleId: itemId, productIds: [product.modelID] },
                updater: (store) => {
                    const productRecord = store.get(product.id);
                    if (productRecord) {
                        productRecord.setValue(null, 'quoteRule');
                    }
                },
                onCompleted: () => setUnassigningItemId(null),
                onError: onFail
            });
        }
    };

    const handleDisableBulkVariation = () => {
        const rule = toolDataQuery?.productMultipleVariantsRule;
        const existingOptions = rule?.options
            ? (() => {
                  try {
                      return JSON.parse(rule.options);
                  } catch {
                      return {};
                  }
              })()
            : {};
        const updatedOptions = { ...existingOptions, enabled: false, salesChannelSpecific: undefined };

        commitDisableBulkVariation({
            variables: {
                id: toGlobalID(product.modelID, 'Model'),
                input: {
                    attributeCodes: [],
                    nameAndNumberEnabled: rule?.nameAndNumberEnabled ?? false,
                    skipOptionForNameAndNumber: rule?.skipOptionForNameAndNumber ?? false,
                    bulkAsNameAndNumberEnabled: rule?.bulkAsNameAndNumberEnabled ?? false,
                    enableImportCSV: rule?.enableImportCSV ?? false,
                    options: JSON.stringify(updatedOptions)
                }
            },
            updater: (store) => store.invalidateStore(),
            onCompleted: () => {
                startUnassignTransition(() => setDataRefreshKey((k) => k + 1));
            }
        });
    };

    const handleAssignShapes = () => {
        const assignments = toolDataQuery?.shapesAssignments;
        const currentProducts = (assignments?.productsIDs as string[]) || [];
        const currentPrintTypes = (assignments?.printTypesIDs as string[]) || [];

        if (currentProducts.includes(product.id)) return;

        commitUnassignShapes({
            variables: {
                productsIds: [...currentProducts, product.id],
                printTypesIds: currentPrintTypes
            },
            updater: (store) => store.invalidateStore(),
            onCompleted: () => {
                startUnassignTransition(() => setDataRefreshKey((k) => k + 1));
            }
        });
    };

    const handleUnassignShapes = () => {
        const assignments = toolDataQuery?.shapesAssignments;
        const remainingProducts = ((assignments?.productsIDs as string[]) || []).filter((id) => id !== product.id);
        const remainingPrintTypes = ((assignments?.printTypesIDs as string[]) || []).filter(
            (id) => !(product.printTypes || []).some((pt) => toGlobalID(pt.printTypeID, 'PrintType') === id)
        );

        commitUnassignShapes({
            variables: {
                productsIds: remainingProducts,
                printTypesIds: remainingPrintTypes
            },
            updater: (store) => store.invalidateStore(),
            onCompleted: () => {
                startUnassignTransition(() => setDataRefreshKey((k) => k + 1));
            }
        });
    };

    // ========== Render Content ==========

    const headerContent = (
        <PanelHeaderContent>
            <PanelHeaderImg>
                <img src={product.smallImageUrl || NoImage} alt={product.name} />
            </PanelHeaderImg>
            <PanelHeaderInfo>
                <h2>{product.name}</h2>
                <HeaderMeta>
                    <span>{product.code}</span>
                </HeaderMeta>
            </PanelHeaderInfo>
        </PanelHeaderContent>
    );
    const fullScreenHeaderContent = (
        <PanelHeaderContent>
            <PanelHeaderImg>
                <img src={product.smallImageUrl || NoImage} alt={product.name} />
            </PanelHeaderImg>
            <PanelHeaderInfo>
                <h2>{product.name}</h2>
                <HeaderMeta>
                    <span>{product.code}</span>
                    <HeaderSep>&middot;</HeaderSep>
                    <ToolBadge $type={product.zakekeEnvironment}>{getToolLabel(product.zakekeEnvironment)}</ToolBadge>
                    <StatusBadge $online={isOnline}>{isOnline ? T._('Online', 'Admin') : T._('Not published', 'Admin')}</StatusBadge>
                    {salesChannelCount > 0 && (
                        <>
                            <HeaderSep>&middot;</HeaderSep>
                            <span>
                                {salesChannelCount} {salesChannelCount === 1 ? T._('Sales Channel', 'Admin') : T._('Sales Channels', 'Admin')}
                            </span>
                        </>
                    )}
                </HeaderMeta>
            </PanelHeaderInfo>
        </PanelHeaderContent>
    );
    const footerContent = (
        <PanelFooter>
            <FooterButton $primary onClick={handleEditProduct}>
                {T._('Edit Product', 'Admin')}
            </FooterButton>
            {onPublishToggle && (
                <FooterButton $publish={!isOnline} $unpublish={isOnline} onClick={handlePublishToggle} disabled={isPublishing}>
                    {isPublishing ? '...' : isOnline ? T._('Unpublish', 'Admin') : T._('Publish', 'Admin')}
                </FooterButton>
            )}
            {onDuplicate && (
                <FooterButton
                    disabled={!isDuplicateEnabled}
                    title={!isDuplicateEnabled ? T._('This product cannot be duplicated', 'Admin') : undefined}
                    onClick={() => {
                        if (!isDuplicateEnabled) return;
                        onClose();
                        onDuplicate();
                    }}
                >
                    {T._('Duplicate', 'Admin')}
                </FooterButton>
            )}
            {onDelete && (
                <FooterButton $danger onClick={() => setConfirmingDelete(true)}>
                    {T._('Delete', 'Admin')}
                </FooterButton>
            )}
        </PanelFooter>
    );
    return (
        <>
            <SidePanelDrawer
                isOpen={true}
                onClose={onClose}
                header={headerContent}
                headerActions={
                    onPreview && product.zakekeEnvironment !== 'VIEWER' ? (
                        <HeaderPreviewButton onClick={onPreview}>{T._('Preview', 'Admin')}</HeaderPreviewButton>
                    ) : undefined
                }
                showFullScreenButton={product.zakekeEnvironment !== 'VIEWER'}
                fullScreenHeader={fullScreenHeaderContent}
                footer={footerContent}
                isDimmed={!!stackedEditor || !!activeAddPanel || !!helpPanel}
                width={580}
                overlayBackground='rgba(0, 0, 0, 0.08)'
                skipAnimation
            >
                {({ isFullScreen }) =>
                    isFullScreen ? (
                        <FullScreenOverview
                            product={product}
                            productStructureSummary={productStructureSummary}
                            availableTools={availableTools}
                            toolData={toolData}
                        />
                    ) : (
                        <SidePanelScrollArea>
                            {product.zakekeEnvironment !== 'VIEWER' && (
                                <StatusBarSection
                                    zakekeEnvironment={product.zakekeEnvironment}
                                    isOnline={isOnline}
                                    salesChannelCount={getSalesChannelCount(product)}
                                    salesChannels={salesChannels}
                                    channelsOpen={channelsOpen}
                                    onToggleChannels={() => setChannelsOpen(!channelsOpen)}
                                    onSalesChannelAction={salesChannelAction}
                                    salesChannelActionLabel={salesChannelActionLabel}
                                    onDisconnectSalesChannel={handleDisconnectSalesChannel}
                                />
                            )}
                            {product.zakekeEnvironment === 'COMPOSER' && (
                                <Suspense fallback={null}>
                                    <ConfiguratorStructureSection
                                        product={product}
                                        hasOptionPrice={hasOptionPrice}
                                        toolData={toolData}
                                        accountPricingRules={accountPricingRules || []}
                                        expandAllKey={expandAllKey}
                                        openSections={openSections}
                                        isUnassigning={isUnassigning}
                                        unassigningItemId={unassigningItemId}
                                        onOpenSectionChange={(toolKey, isOpen) => {
                                            if (isOpen) {
                                                setOpenSections(new Set([toolKey]));
                                            } else {
                                                setOpenSections((prev) => {
                                                    const next = new Set(prev);
                                                    next.delete(toolKey);
                                                    return next;
                                                });
                                            }
                                        }}
                                        onOpenToolEditor={handleOpenToolEditor}
                                        onUnassignTool={handleUnassignTool}
                                        onShowAddPanel={setActiveAddPanel}
                                        onGoToFullTool={handleGoToFullTool}
                                        onShowHelpPanel={(url) => {
                                            const match = url.match(/\/articles\/(\d+)/);
                                            if (match) setHelpPanel({ articleId: match[1], helpUrl: url });
                                        }}
                                    />
                                </Suspense>
                            )}
                            {product.zakekeEnvironment === 'CUSTOMIZER' && (
                                <CustomizerStructureSection
                                    productId={product.modelID}
                                    productStructureSummary={productStructureSummary}
                                    productColors={productColors}
                                    allProductColors={allProductColors}
                                    onManageProduct={() => {
                                        if (product.provider !== 'ZAKEKE') {
                                            handleGoToFullTool('/product?product_id=' + product.modelID);
                                            return;
                                        }
                                        handleGoToFullTool('/product/print-area/?product_id=' + product.modelID);
                                    }}
                                    onPrintAreaClick={(colorID, sideID) => {
                                        if (product.provider !== 'ZAKEKE') {
                                            handleGoToFullTool('/product?product_id=' + product.modelID);
                                            return;
                                        }
                                        handleGoToFullTool(
                                            productColors.length === 0
                                                ? `/product/print-area/?product_id=${product.modelID}&sideId=${sideID}`
                                                : `/product/variations/list/?product_id=${product.modelID}&variantId=${colorID}&sideId=${sideID}`
                                        );
                                    }}
                                />
                            )}
                            {product.zakekeEnvironment === 'VIEWER' && (
                                <Suspense fallback={null}>
                                    <ViewerStructureSection productId={product.id} />
                                </Suspense>
                            )}
                            <ToolCategorySections
                                product={product}
                                toolData={toolData}
                                availableTools={availableTools}
                                accountPrintMethods={accountPrintMethods || []}
                                accountPricingRules={accountPricingRules || []}
                                accountHasRules={accountHasRules}
                                expandAllKey={expandAllKey}
                                openSections={openSections}
                                hideUnconfigured={hideUnconfigured}
                                isUnassigning={isUnassigning}
                                unassigningItemId={unassigningItemId}
                                onOpenSectionChange={(toolKey, isOpen) => {
                                    if (isOpen) {
                                        setOpenSections(new Set([toolKey]));
                                    } else {
                                        setOpenSections((prev) => {
                                            const next = new Set(prev);
                                            next.delete(toolKey);
                                            return next;
                                        });
                                    }
                                }}
                                onHideUnconfiguredChange={(checked) => {
                                    setHideUnconfigured(checked);
                                    try {
                                        localStorage.setItem('zakeke_hideUnconfiguredTools', String(checked));
                                    } catch {
                                        /* */
                                    }
                                }}
                                onCollapseAll={() => {
                                    setOpenSections(new Set());
                                    setExpandAllKey((k) => k + 1);
                                }}
                                onOpenToolEditor={handleOpenToolEditor}
                                onUnassignTool={handleUnassignTool}
                                onDisableBulkVariation={handleDisableBulkVariation}
                                onAssignShapes={handleAssignShapes}
                                onUnassignShapes={handleUnassignShapes}
                                onShowAddPanel={setActiveAddPanel}
                                onGoToFullTool={handleGoToFullTool}
                                onNavigate={(url, state) => navigate(url, { state: { ...state, ...sidebarNavState } })}
                                onEnablePlacement={handleEnablePlacement}
                                onShowHelpPanel={(url) => {
                                    const match = url.match(/\/articles\/(\d+)/);
                                    if (match) setHelpPanel({ articleId: match[1], helpUrl: url });
                                }}
                            />
                            {confirmingDelete && (
                                <DeleteConfirmationOverlay
                                    productName={product.name}
                                    onCancel={() => setConfirmingDelete(false)}
                                    onConfirm={() => {
                                        setConfirmingDelete(false);
                                        onDelete!();
                                    }}
                                />
                            )}
                        </SidePanelScrollArea>
                    )
                }
            </SidePanelDrawer>
            <StackedEditorPanel
                stackedEditor={stackedEditor}
                product={product}
                toolDefinitions={TOOL_DEFINITIONS}
                onCloseStacked={handleCloseStacked}
                onGoToFullTool={handleGoToFullTool}
                onNavigate={(url) => navigate(backofficeUrl(url), { state: sidebarNavState })}
            />
            <AddRulePanels
                activeAddPanel={activeAddPanel}
                product={product}
                toolData={toolData}
                onCloseAddPanel={() => setActiveAddPanel(null)}
                onRefreshData={() => {
                    setTimeout(() => {
                        commitLocalUpdate(relayEnvironment, (store) => store.invalidateStore());
                        startUnassignTransition(() => setDataRefreshKey((k) => k + 1));
                    }, 500);
                }}
                onNavigate={(url, state) => navigate(backofficeUrl(url), { state: { ...state, ...sidebarNavState } })}
                onOpenToolEditor={handleOpenToolEditor}
            />
            {helpPanel && <HelpArticlePanel articleId={helpPanel.articleId} helpUrl={helpPanel.helpUrl} onClose={() => setHelpPanel(null)} />}
        </>
    );
};
